import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { FlightViewTypes } from 'constants/flight'
import StandaloneFlightStateContext from 'contexts/Flights/StandaloneFlights/standaloneFlightsStateContext'

type SingleLegPriceCaptionTripType = 'oneWay' | 'return' | 'multiCity'

function getSingleLegPriceCaptionTripType(
  fareType: App.JourneyV2['fareType'],
  viewType: string | undefined,
  isMultiCity?: boolean,
): SingleLegPriceCaptionTripType {
  if (
    isMultiCity ||
    viewType === FlightViewTypes.MULTI_CITY ||
    fareType === FlightViewTypes.MULTI_CITY
  ) {
    return 'multiCity'
  }

  const isReturnSearch = viewType === FlightViewTypes.RETURN ||
    viewType === FlightViewTypes.TWO_ONE_WAYS ||
    viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN ||
    fareType === FlightViewTypes.RETURN

  if (isReturnSearch) {
    return 'return'
  }

  return 'oneWay'
}

interface Options {
  fareType: App.JourneyV2['fareType']
  isMultiCity?: boolean
  isRelativePrice: boolean
  isStandalone: boolean
  /**
   * Whether the non-standalone (all-travellers) case is genuinely the bundled hotel + flight
   * flow, as distinct from `isStandalone` being false for a standalone mixed-traveller search.
   * Selects the all-travellers caption: only the bundled flow shows "Hotel and return flight
   * total" - a standalone mixed-traveller search has no hotel and must keep the generic
   * "For all travellers" caption. Defaults to `!isStandalone` for callers where the two cases
   * aren't distinguished.
   */
  isBundledFlow?: boolean
}

function useSingleLegFlightTilePriceCaption({
  fareType,
  isMultiCity,
  isRelativePrice,
  isStandalone,
  isBundledFlow = !isStandalone,
}: Options): string | undefined {
  const intl = useIntl()
  const { viewType, isMixedTravellerSearch } = useContext(StandaloneFlightStateContext)
  const tripType = getSingleLegPriceCaptionTripType(fareType, viewType, isMultiCity)

  return useMemo(() => {
    if (isStandalone && !isRelativePrice && isMixedTravellerSearch) {
      return intl.formatMessage({
        id: 'NfM1-w_GqHGB1iUMbNuZ',
        defaultMessage: 'For all travellers',
        description: 'Caption indicating the price is for all travellers',
      })
    }

    if (isStandalone && !isRelativePrice) {
      if (tripType === 'oneWay') {
        return intl.formatMessage({
          id: 'YS7_HKoA7zvrdkIplbe4',
          defaultMessage: 'One-way per adult',
          description: 'Price caption under a one-way standalone flight tile fare for the per adult amount',
        })
      }

      if (tripType === 'multiCity') {
        return intl.formatMessage({
          id: 'mzKvXsl2UmWPhrFlaocG',
          defaultMessage: 'Multi-city trip per adult',
          description: 'Price caption under a multi-city standalone flight tile fare for the per adult amount',
        })
      }

      return intl.formatMessage({
        id: 'faHtgeX6p6QBclbutc4A',
        defaultMessage: 'Return per adult',
        description: 'Price caption under a return standalone flight tile fare for the per adult amount',
      })
    }

    if (!isStandalone && isBundledFlow) {
      return intl.formatMessage({
        id: 'lyGoDh2tSyCxTve2_7G5',
        defaultMessage: 'Hotel and return flight total',
        description: 'Caption indicating the bundled price covers the hotel and return flight total for all travellers',
      })
    }

    if (!isStandalone) {
      return intl.formatMessage({
        id: 'NfM1-w_GqHGB1iUMbNuZ',
        defaultMessage: 'For all travellers',
        description: 'Caption indicating the price is for all travellers',
      })
    }

    return undefined
  }, [intl, isRelativePrice, isStandalone, isBundledFlow, isMixedTravellerSearch, tripType])
}

export default useSingleLegFlightTilePriceCaption
